import type { DensitySnapshot, InstrumentRef } from "@screener/protocol";
import { type CardRowOrder, type DensityCard, instrumentKey } from "./cards";
import type { DensityFilters, DensitySort, ScreenMode } from "./derive";
import type { DisplayMinimumsState } from "./display-minimums";
import { sameStringList } from "./use-stable";

/**
 * Заморозка вида «Карточки» §77: расположение (порядок карточек, их колонки,
 * порядок строк) запоминается снимком, а числа в строках остаются живыми.
 * Чистый модуль без React — тем же приёмом, что `cards.ts`.
 */

/** Карточка снимка: расположение и последний известный снимок каждой строки. */
export interface FrozenCard {
  key: string;
  instrument: InstrumentRef;
  /** Флаг закрепления §65 — единственное, что меняется в снимке после нажатия. */
  pinned: boolean;
  hiddenCount: number;
  rows: readonly DensitySnapshot[];
}

export type FrozenCards = readonly FrozenCard[];

/** Снимок расположения из карточек, как их построил `buildCards` (design Д1). */
export function freezeCards(cards: readonly DensityCard[]): FrozenCards {
  return cards.map((card) => ({
    key: card.key,
    instrument: card.instrument,
    pinned: card.pinned,
    hiddenCount: card.hiddenCount,
    rows: [...card.rows],
  }));
}

export interface AppliedFreeze {
  cards: DensityCard[];
  /** Видимые (после фильтров) плотности, которых нет в снимке: появятся при разморозке. */
  newCount: number;
}

/**
 * Накладывает живые величины на снимок расположения (design Д1, Д2).
 *
 * - `liveById` — все живые плотности потока до фильтров экрана: плотность, по
 *   новым величинам выпавшая из фильтра, остаётся живой, а не «ушедшей».
 * - `visibleIds` — плотности после фильтров: только по ним считается `newCount`.
 * - `lastSeen` — последний живой снимок строки, виденный экраном после
 *   нажатия; ушедшая строка показывает его, а без него — снимок момента
 *   нажатия.
 *
 * Строки и карточки идут в порядке снимка. Плотность, которой в снимке нет, на
 * экран не попадает, в том числе новой карточкой.
 */
export function applyFreeze(
  frozen: FrozenCards,
  liveById: ReadonlyMap<string, DensitySnapshot>,
  visibleIds: Iterable<string>,
  lastSeen: ReadonlyMap<string, DensitySnapshot> = new Map(),
): AppliedFreeze {
  const frozenIds = new Set<string>();
  const cards = frozen.map((card): DensityCard => {
    const endedIds = new Set<string>();
    const rows = card.rows.map((row) => {
      frozenIds.add(row.density_id);
      const live = liveById.get(row.density_id);
      if (live !== undefined) {
        return live;
      }
      endedIds.add(row.density_id);
      return lastSeen.get(row.density_id) ?? row;
    });
    return {
      key: card.key,
      instrument: card.instrument,
      rows,
      // Нечисловой объём в максимум не входит — как в `buildCards`.
      maxUsd: Math.max(
        0,
        ...rows.map((row) => Number(row.density_usd)).filter((usd) => Number.isFinite(usd)),
      ),
      pinned: card.pinned,
      hiddenCount: card.hiddenCount,
      endedIds,
    };
  });

  let newCount = 0;
  for (const id of visibleIds) {
    if (!frozenIds.has(id)) {
      newCount += 1;
    }
  }
  return { cards, newCount };
}

/**
 * Убирает карточки монеты со снимка (§77 п. 5: скрытие действует сразу, а
 * заморозка остаётся). Монета — по приведённой базе, как и само скрытие §75:
 * уходят все её карточки на всех биржах. Нечего убирать — тот же снимок.
 */
export function dropCoin(frozen: FrozenCards, base: string): FrozenCards {
  const kept = frozen.filter((card) => card.instrument.base !== base);
  return kept.length === frozen.length ? frozen : kept;
}

/**
 * Флаги закрепления карточек снимка по ключам закреплённых (§77 п. 5):
 * значок меняется сразу, а карточка не переезжает к закреплённым до
 * разморозки. Флаги уже верны — тот же снимок.
 */
export function setPinnedFlags(
  frozen: FrozenCards,
  pinnedKeys: ReadonlySet<string>,
  liveHiddenCounts: ReadonlyMap<string, number> = new Map(),
): FrozenCards {
  if (frozen.every((card) => card.pinned === pinnedKeys.has(card.key))) {
    return frozen;
  }
  return frozen.map((card) => {
    const pinned = pinnedKeys.has(card.key);
    if (card.pinned === pinned) {
      return card;
    }
    // `hiddenCount` есть только у закреплённой карточки: у открепленной он 0,
    // у только что закреплённой — живое число скрытых фильтрами плотностей.
    return { ...card, pinned, hiddenCount: pinned ? (liveHiddenCounts.get(card.key) ?? 0) : 0 };
  });
}

/**
 * Сколько живых плотностей каждого инструмента скрыто фильтрами: все живые
 * за вычетом видимых — та же мера, что у закреплённой карточки в `buildCards`.
 */
export function hiddenCountsByKey(
  liveRows: readonly DensitySnapshot[],
  visibleRows: readonly DensitySnapshot[],
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const row of liveRows) {
    const key = instrumentKey(row.instrument);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  for (const row of visibleRows) {
    const key = instrumentKey(row.instrument);
    counts.set(key, Math.max(0, (counts.get(key) ?? 0) - 1));
  }
  return counts;
}

/**
 * Строки, которые замороженный вид показывает на экране: живые и ушедшие
 * последним снимком, в порядке карточек. По ним сопоставляются закрепления —
 * строка, которой нет среди отфильтрованных, не должна терять значок.
 */
export function shownRows(cards: readonly DensityCard[]): DensitySnapshot[] {
  return cards.flatMap((card) => card.rows);
}

/**
 * Входы, смена любого из которых размораживает вид (§77 п. 5): сортировка,
 * фильтры, личные минимумы, порядок строк карточки и вид экрана. Фильтры
 * и минимумы сравниваются по значению, прочее — по ссылке: эти меняются
 * только действием пользователя.
 */
export interface FreezeDeps {
  sort: DensitySort;
  filters: DensityFilters;
  displayMinimums: DisplayMinimumsState;
  rowOrder: CardRowOrder;
  mode: ScreenMode;
}

/**
 * Фильтры равны по значению: поле «Минимальный размер» пересоздаёт объект
 * фильтров на каждую правку, в том числе не меняющую значение, и такая правка
 * вид размораживать не должна.
 */
function sameFilters(left: DensityFilters, right: DensityFilters): boolean {
  if (left === right) {
    return true;
  }
  const sameExchanges =
    left.exchanges === right.exchanges ||
    (left.exchanges !== null &&
      right.exchanges !== null &&
      sameStringList(left.exchanges, right.exchanges));
  return (
    left.minUsd === right.minUsd &&
    sameStringList(left.states, right.states) &&
    sameExchanges &&
    left.hideMm === right.hideMm &&
    left.onlyFavorites === right.onlyFavorites
  );
}

/**
 * Минимумы равны по действующим значениям: первая загрузка и перечитывание
 * пересоздают состояние без действия пользователя, и вид размораживается,
 * только если изменились сами минимумы. Пока минимумов нет, действует пустой
 * список — так экран и фильтрует.
 */
function sameMinimums(left: DisplayMinimumsState, right: DisplayMinimumsState): boolean {
  if (left === right) {
    return true;
  }
  const values = (state: DisplayMinimumsState) =>
    JSON.stringify(state.status === "ready" ? state.minimums : []);
  return values(left) === values(right);
}

/** Заморозка, поставленная при `frozenDeps`, всё ещё действует при `current`. */
export function isFreezeCurrent(frozenDeps: FreezeDeps, current: FreezeDeps): boolean {
  return (
    frozenDeps.sort === current.sort &&
    sameFilters(frozenDeps.filters, current.filters) &&
    sameMinimums(frozenDeps.displayMinimums, current.displayMinimums) &&
    frozenDeps.rowOrder === current.rowOrder &&
    frozenDeps.mode === current.mode
  );
}
